import React from 'react';
import type { ApprovalView } from '../../api/endpoints';
import type { Tokens } from '../../theme/tokens';

// Where an approval request stands: each level with who may approve it, the
// history of decisions (with resets when the record was edited), and — while
// it is open — whether the signed-in user may act and, if not, why.

const when = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

/** A remark or reason on its own line, keeping the line breaks it was typed with. */
const Remark = ({ label, text, t, color }: { label: string; text?: string | null; t: Tokens; color?: string }) => (
  text ? (
    <div style={{
      marginTop: '4px', padding: '4px 8px', borderLeft: `3px solid ${t.border}`, color: color || t.textMain,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: t.fontSize.sm
    }}>
      <strong>{label}:</strong> {text}
    </div>
  ) : null
);

const statusTitle = (a: ApprovalView) =>
  a.status === 'APPROVED' ? 'Approved'
    : a.status === 'REJECTED' ? 'Rejected'
      : a.status === 'WITHDRAWN' ? 'Withdrawn'
        : a.status === 'CANCELLED' ? 'Cancelled'
          : `Level ${a.nextLevel} of ${a.levelsRequired}`;

const ApprovalProgress = ({ approval, t }: { approval: ApprovalView; t: Tokens }) => {
  const open = approval.status === 'PENDING';
  // Decisions since the last reset decide which levels are done.
  const lastReset = approval.steps.map((s) => s.decision).lastIndexOf('RESET');
  const current = approval.steps.slice(lastReset + 1);
  // Withdrawn, or closed by the system (record deleted, replaced by a newer edit).
  const closing = current.find((s) => s.decision === 'WITHDRAWN' || s.decision === 'CANCELLED');

  const levelLine = (level: number, approvers: string[]) => {
    const done = current.find((s) => s.level === level && s.decision === 'APPROVED');
    const rejected = current.find((s) => s.level === level && s.decision === 'REJECTED');
    const isNext = open && approval.nextLevel === level;
    const icon = done ? '✅' : rejected ? '❌' : isNext ? '🕒' : '◻️';
    return (
      <div key={level} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '6px 0' }}>
        <span style={{ fontSize: '16px', lineHeight: '20px' }}>{icon}</span>
        <div>
          <strong style={{ color: t.textMain }}>Level {level}</strong>
          <span style={{ color: t.textMuted }}> · {approvers.join(' or ')}</span>
          {done && (
            <div style={{ color: t.textMuted, fontSize: t.fontSize.sm }}>
              Approved by {done.byName || done.byUsername}{done.byRole ? ` (${done.byRole})` : ''} · {when(done.at)}
              {done.override && (
                <em>{String(done.byUsername || '').toLowerCase() === String(approval.requestedBy || '').toLowerCase()
                  ? ' · administrator approved their own request (emergency option)'
                  : ' · administrator override'}</em>
              )}
              <Remark label="Remark" text={done.comment} t={t} />
            </div>
          )}
          {rejected && (
            <div style={{ color: t.danger, fontSize: t.fontSize.sm }}>
              Rejected by {rejected.byName || rejected.byUsername}{rejected.byRole ? ` (${rejected.byRole})` : ''} · {when(rejected.at)}
              <Remark label="Reason" text={rejected.comment} t={t} />
            </div>
          )}
          {isNext && <div style={{ color: t.warning, fontSize: t.fontSize.sm }}>Waiting for approval</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      border: `1px solid ${t.border}`, borderRadius: t.radius.lg, padding: '12px 14px',
      backgroundColor: t.bgSubtle, marginBottom: '14px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <strong style={{ color: t.textMain }}>
          Approval · {statusTitle(approval)}
        </strong>
        <span style={{ color: t.textMuted, fontSize: t.fontSize.sm }}>
          Requested by {approval.requestedBy} · {when(approval.requestedAt)}
        </span>
      </div>
      {approval.levels.map((l) => levelLine(l.level, l.approvers))}
      {approval.steps.some((s) => s.decision === 'RESET') && (
        <div style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '4px' }}>
          ↺ Sent back to level 1 after a change
          ({approval.steps.filter((s) => s.decision === 'RESET').map((s) => `${s.comment || 'edited'}, ${when(s.at)}`).join('; ')}).
        </div>
      )}
      {closing && (
        <div style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '6px' }}>
          {closing.decision === 'WITHDRAWN'
            ? <>↩ Withdrawn by {closing.byName || closing.byUsername}{closing.byRole ? ` (${closing.byRole})` : ''} · {when(closing.at)}</>
            : <>⊘ Cancelled · {when(closing.at)}{closing.byName || closing.byUsername ? ` (by ${closing.byName || closing.byUsername})` : ''}</>}
          <Remark label={closing.decision === 'WITHDRAWN' ? 'Reason' : 'Why'} text={closing.comment} t={t} />
        </div>
      )}
      {open && !approval.canAct && approval.whyNot && (
        <div style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '8px', fontStyle: 'italic' }}>
          You cannot act on this now: {approval.whyNot}
        </div>
      )}
    </div>
  );
};

export default ApprovalProgress;
